import pkg from '../../../package.json';
import backgroundImage from '../../assets/gr-image-signin.svg';

import { CoreContext, EmptyState } from '@scmlatam/web-ui';
import type { Language, RouteItem } from '@scmlatam/web-ui';

import { esES as DGEsEs, enUS as DGEnUs } from '@mui/x-data-grid/locales';
import { esES as coreEsES, enUS as coreEnUS } from '@mui/material/locale';
import { esES as pickersEsES, enUS as pickersEnUS } from '@mui/x-date-pickers/locales';
import { useContext, useEffect } from 'react';
import { signServices } from '../../services/sign';

import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import { useTranslation } from 'react-i18next';
import { useSideBar } from './useSidebar';
import { useShortcut } from './useShortcut';
import { Icon, Typography } from '@mui/material';
import { useAuthorization } from '../../contexts/AuthorizationContext';
import { normalizeView } from '../../auth/authorization';
import { SubPage as SPEmployees } from '../../modules/timekeeper/employee/pages/SubPage';
import { SubPage as SPSchedule } from '../../modules/timekeeper/schedule/pages/SubPage';
import { SubPage as SPRunReports } from '../../modules/reports/results/pages/SubPage';
import { Stepper } from '../../modules/reports/execute/components/Stepper';
//import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

const HomePage = () => {
    useEffect(() => {
        if (localStorage.getItem('authenticated') === 'true') {
            window.location.href = '/timekeeper?module=timekeeper&view=workers';
        }
    }, []);

    return <>redirecionando...</>;
};

const TimekeeperModule = () => {
    const searchParams = new URLSearchParams(location.search);
    const view = searchParams.get('subpage') ?? searchParams.get('view');
    const { canView, isLoading } = useAuthorization();

    if (isLoading) {
        return null;
    }

    if (!view || !['workers', 'employees', 'timecard', 'schedule', 'scheduler'].includes(view)) {
        return <EmptyState variant="not-found" context="page" />;
    }

    const requiredView = normalizeView(`/timekeeper?subpage=${view}`);

    if (!canView(requiredView)) {
        return <EmptyState variant="no-permission" permissions={[requiredView]} context="page" />;
    }

    switch (view) {
        case 'workers':
        case 'employees':
            return <SPEmployees />;
        case 'timecard':
            return <>timecard</>;
        case 'schedule':
        case 'scheduler':
            return <SPSchedule />;
        default:
            return <EmptyState variant="not-found" context="page" />;
    }
};

const ReportsModule = () => {
    const searchParams = new URLSearchParams(location.search);
    const view = searchParams.get('subpage') ?? searchParams.get('view');
    const { canView, isLoading } = useAuthorization();

    if (isLoading) {
        return null;
    }

    if (!view || !['results_report', 'resume', 'execute_report', 'execute_reports'].includes(view)) {
        return <EmptyState variant="not-found" context="page" />;
    }

    const requiredView = normalizeView(`/reports?subpage=${view}`);

    if (!canView(requiredView)) {
        return <EmptyState variant="no-permission" permissions={[requiredView]} context="page" />;
    }

    switch (view) {
        case 'results_report':
        case 'resume':
            return <SPRunReports />;
        case 'execute_report':
        case 'execute_reports':
            return <Stepper />;
        default:
            return <EmptyState variant="not-found" context="page" />;
    }
};

const useCore = () => {
    const { t, i18n } = useTranslation('core.login.form');
    const { items, tooltips } = useSideBar();
    const { singleShortcuts, rangeShortcuts, dateFormat } = useShortcut();

    const { user, pass, setIsAuthenticated, setLoadingAuth, redirect, setOpenUnsaved } = useContext(CoreContext);
    const { clear, hydrate } = useAuthorization();

    const logout = () => {
        signServices.logout(setIsAuthenticated);
        clear();
    };

    const login = async (navigate: (to: string) => void) => {
        const session = await signServices.login(user, pass, setLoadingAuth, setIsAuthenticated, navigate);

        if (session) {
            hydrate(session.accessToken, session.permissionCatalog);
            localStorage.setItem('username', user);
        }
    };

    const sidebar = {
        items: items,
        signOut: {
            text: 'Cerrar sesión',
            disabled: false,
            icon: (
                <Icon
                    baseClassName="material-symbols-rounded"
                    sx={{
                        color: '#9a28e6',
                        fontSize: '28px',
                    }}
                >
                    logout
                </Icon>
            ),
            onClick: logout,
        },
        tooltips: tooltips,
    };

    const account = {
        username: localStorage.getItem('username') ? localStorage.getItem('username') : user,
        avatarUrl: 'https://i.pravatar.cc/300',
        options: [],
    };

    const product = {
        name: t('product_name'),
        version: pkg.version,
        backgroundImage: backgroundImage,
    };

    const routes: RouteItem[] = [
        {
            path: '/',
            element: <HomePage />,
        },
        {
            path: '/timekeeper',
            element: <TimekeeperModule />,
        },
        {
            path: '/reports',
            element: <ReportsModule />,
        },
    ];

    const languages: Language[] = [
        {
            name: 'Español',
            flagUrl: 'https://flagcdn.com/es.svg',
            code: 'es',
            active: i18n.language === 'es',
            theme: {
                dg: DGEsEs,
                pickers: pickersEsES,
                core: coreEsES,
            },
            onClick: () => {
                i18n.changeLanguage('es');
            },
        },
        {
            name: 'English',
            flagUrl: 'https://flagcdn.com/us.svg',
            code: 'en',
            active: i18n.language === 'en',
            theme: {
                dg: DGEnUs,
                pickers: pickersEnUS,
                core: coreEnUS,
            },
            onClick: () => {
                i18n.changeLanguage('en');
            },
        },
    ];

    const loginForm = {
        title: t('title'),
        labels: {
            username: t('labels.username'),
            password: t('labels.password'),
        },
        placeholder: {
            username: t('placeholders.username'),
            password: t('placeholders.password'),
        },
        divider: t('or_continue_with'),
        buttons: {
            signIn: {
                text: t('buttons.signIn'),
                icon: null,
                disabled: true,
                onClick: (navigate: (to: string) => void) => login(navigate),
            },
            google: {
                text: null,
                icon: <GoogleIcon />,
                disabled: true,
                onClick: () => {},
            },
            microsoft: {
                text: null,
                icon: <MicrosoftIcon />,
                disabled: true,
                onClick: () => {},
            },
        },
        version: t('version'),
    };

    const signup = {
        title: t('title', { ns: 'core.signup.form' }),
        labels: {
            username: t('labels.username', { ns: 'core.signup.form' }),
            password: t('labels.password', { ns: 'core.signup.form' }),
            email: t('labels.email', { ns: 'core.signup.form' }),
        },
        placeholder: {
            username: t('placeholders.username', { ns: 'core.signup.form' }),
            password: t('placeholders.password', { ns: 'core.signup.form' }),
            email: t('placeholders.email', { ns: 'core.signup.form' }),
        },
        buttons: {
            signUp: {
                text: t('buttons.signUp', { ns: 'core.signup.form' }),
                icon: null,
                disabled: false,
                onClick: () => {},
            },
        },
        version: t('version', { ns: 'core.signup.form' }),
    };

    const unsavedChangesAlert = {
        title: (
            <Typography
                variant="h6"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <Icon
                    baseClassName="material-symbols-rounded"
                    sx={{
                        fontSize: '30px',
                        color: '#FB6000',
                    }}
                >
                    warning
                </Icon>
                <span>Cambios sin guardar</span>
            </Typography>
        ),
        message: '¿Estás seguro de que deseas salir sin guardar cambios?',
        actionButton: {
            buttonText: 'Salir',
            disabled: false,
            loading: false,
            onClick: () => {
                window.location.href = redirect;

                localStorage.setItem('history_path', JSON.stringify([...JSON.parse(localStorage.getItem('history_path') || '[]'), redirect]));

                localStorage.setItem('save_redirect', 'false');
                setOpenUnsaved(false);
            },
        },
        cancelButton: {
            buttonText: 'Cancelar',
            disabled: false,
            loading: false,
            onClick: () => {
                localStorage.setItem('save_redirect', 'false');
                setOpenUnsaved(false);
            },
        },
    };

    useEffect(() => {
        //setIsAuthenticated(false);
    }, []);

    return {
        sidebar,
        singleShortcuts,
        rangeShortcuts,
        dateFormat,
        account,
        product,
        routes,
        languages,
        loginForm,
        signup,
        unsavedChangesAlert,
    };
};

export { useCore };
